// Use the actual Dataset ID, not its numeric display name in Events Manager.
const PIXEL_ID = "971525082081651";

const PURCHASE_STORAGE_PREFIX = "publizm-meta-purchase:";

type MetaEvent = "ViewContent" | "InitiateCheckout" | "CompleteRegistration";
type MetaEventData = { content_ids: string[]; content_type: "product"; format?: string };
type PurchaseData = { content_ids: string[]; content_type: "product"; value: number; currency: "NOK" };
type PixelEvent = {
    event: "PageView" | MetaEvent | "Purchase";
    data?: MetaEventData | PurchaseData;
    url: string;
    eventId?: string;
    orderId?: string;
};
export type MetaPurchase = { orderId: string; bookId: string; valueNok: number };
type SendEvent = {
    documentLink: string;
    referrerLink: string;
    customParams: { get: (name: string) => string | null };
};
type FbqArgument = string | MetaEventData | PurchaseData | Record<string, never> | { eventID: string };

type Fbq = ((...args: FbqArgument[]) => void) & {
    queue: FbqArgument[][];
    push: Fbq;
    loaded: boolean;
    version: string;
    disablePushState: boolean;
    callMethod?: (...args: FbqArgument[]) => void;
    getFbeventsModules?: (name: string) => { listen?: (listener: (event: SendEvent) => boolean) => void };
};

declare global {
    interface Window {
        fbq?: Fbq;
        _fbq?: Fbq;
    }
}

let enabled = false;
let initialized = false;
let scriptReady = false;
let scriptFailed = false;
let pendingEvents: PixelEvent[] = [];
let lastRoute: string | null = null;
let eventSequence = 0;
const eventNamespace = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const eventUrls = new Map<string, string>();
// Orders whose Purchase is queued or sent in this document.
const purchasedOrders = new Set<string>();

export function getMetaPageRoute(pathname: string, search: string): string {
    const format = new URLSearchParams(search).get("type");
    if (/^\/book-details\/[^/]+\/?$/.test(pathname) && (format === "audio" || format === "ebok")) {
        return `${pathname}?type=${format}`;
    }
    return pathname;
}

function pageUrl(pathname: string, search: string): string {
    return `${window.location.origin}${getMetaPageRoute(pathname, search)}`;
}

function installUrlSanitizer(fbq: Fbq): boolean {
    // Meta's public track API cannot override dl/rl. Fail closed if this internal send hook is unavailable.
    try {
        const sendEvent = fbq.getFbeventsModules?.("SignalsFBEventsSendEventEvent");
        if (!sendEvent?.listen) return false;
        sendEvent.listen((event) => {
            if (typeof event.customParams?.get !== "function") return true;
            const id = event.customParams.get("eid") ?? "";
            const url = eventUrls.get(id);
            eventUrls.delete(id);
            // A truthy return cancels the SDK send, including unrequested automatic events.
            if (!enabled || !url) return true;
            event.documentLink = url;
            event.referrerLink = "";
            return false;
        });
        return true;
    } catch {
        return false;
    }
}

function purchaseStored(orderId: string): boolean {
    try {
        return window.localStorage.getItem(PURCHASE_STORAGE_PREFIX + orderId) !== null;
    } catch {
        return false;
    }
}

function storePurchase(orderId: string): void {
    try {
        window.localStorage.setItem(PURCHASE_STORAGE_PREFIX + orderId, "1");
    } catch {
        // Without storage, the in-memory set and Meta's eventID deduplication still apply.
    }
}

function clearPending(): void {
    pendingEvents.forEach((event) => event.orderId && purchasedOrders.delete(event.orderId));
    pendingEvents = [];
}

function send(event: PixelEvent): void {
    const id = event.eventId ?? `publizm-meta-${eventNamespace}-${++eventSequence}`;
    eventUrls.set(id, event.url);
    // trackSingle permits repeated PageView and targets only the configured Pixel.
    window.fbq?.("trackSingle", PIXEL_ID, event.event, event.data ?? {}, { eventID: id });
    if (event.orderId) storePurchase(event.orderId);
}

function enqueue(event: PixelEvent): void {
    if (scriptReady) send(event);
    else pendingEvents.push(event);
}

async function purchaseEventId(orderId: string): Promise<string> {
    // Deterministic, so Meta and a later Conversions API can deduplicate without receiving the raw orderId.
    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(orderId));
    const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `purchase-${hex}`;
}

function bootstrap(): void {
    if (initialized) return;
    initialized = true;
    const fbq = function (...args: FbqArgument[]) {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue.push(args);
    } as Fbq;
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.disablePushState = true;
    window.fbq = fbq;
    window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.onload = () => {
        if (scriptReady || scriptFailed) return;
        if (!installUrlSanitizer(fbq)) {
            scriptFailed = true;
            clearPending();
            return;
        }
        scriptReady = true;
        fbq("init", PIXEL_ID);
        if (!enabled) {
            clearPending();
            return;
        }
        const events = pendingEvents;
        pendingEvents = [];
        events.forEach(send);
    };
    script.onerror = () => {
        scriptFailed = true;
        clearPending();
    };
    document.head.appendChild(script);
}

export function setMetaPixelEnabled(value: boolean): void {
    if (enabled === value) return;
    enabled = value;
    if (!value) {
        clearPending();
        lastRoute = null;
        return;
    }
    bootstrap();
}

export function trackMetaPageView(route: string): void {
    if (!enabled || scriptFailed) return;
    const url = new URL(route, window.location.origin);
    const identity = getMetaPageRoute(url.pathname, url.search);
    if (lastRoute === identity) return;
    lastRoute = identity;
    enqueue({ event: "PageView", url: pageUrl(url.pathname, url.search) });
}

export function trackMetaEvent(event: MetaEvent, data?: MetaEventData): boolean {
    if (!enabled || !initialized || scriptFailed) return false;
    enqueue({ event, data, url: pageUrl(window.location.pathname, window.location.search) });
    return true;
}

/**
 * Sends Purchase once per orderId for a payment the receipt page has seen as Vipps AUTHORIZED.
 * `valueNok` is in NOK, not øre.
 */
export async function trackMetaPurchase({ orderId, bookId, valueNok }: MetaPurchase): Promise<boolean> {
    if (!enabled || !initialized || scriptFailed) return false;
    if (!orderId || !bookId || !Number.isFinite(valueNok) || valueNok <= 0) return false;
    if (purchasedOrders.has(orderId) || purchaseStored(orderId)) return false;
    // Reserve before awaiting so concurrent calls for the same order cannot both send.
    purchasedOrders.add(orderId);
    const url = pageUrl(window.location.pathname, window.location.search);
    let eventId: string;
    try {
        eventId = await purchaseEventId(orderId);
    } catch {
        purchasedOrders.delete(orderId);
        return false;
    }
    if (!enabled || scriptFailed) {
        purchasedOrders.delete(orderId);
        return false;
    }
    const data: PurchaseData = { content_ids: [bookId], content_type: "product", value: valueNok, currency: "NOK" };
    enqueue({ event: "Purchase", data, url, eventId, orderId });
    return true;
}
